////////////////////////////////////////////////////////////
//                                                        //
//  Program: MasterAccommodationAgreement.jsx             //
//  Application: Property-Setup                           //
//  Option: View and edit the master Guest Accommodation  //
//          Agreement document                            //
//  Date: 2026-07-10                                      //
//                                                        //
////////////////////////////////////////////////////////////

import React, { Component } from "react";
import { Container } from "react-bootstrap";
import renderHTML from "react-render-html";
import JoditEditor from "jodit-react";
import { Jodit } from "jodit";
import DatePicker from "react-datepicker";
import Moment from "moment";
import SimpleReactValidator from "simple-react-validator";
import editIcon from "./../../assets/images/edit-pen-icon.svg";
import MasterSidebar from "../common/MasterSidebar";
import Alerts from "../common/Alerts";
import FileUploadService from "../../services/FileUploadService";
import MasterAccommodationAgreementService from "../../services/MasterAccommodationAgreementService";

export class MasterAccommodationAgreement extends Component {
  constructor(props) {
    super(props);

    this.validator = new SimpleReactValidator({ autoForceUpdate: this });
    this.lastUpdatedOnInputGroupRef = React.createRef();

    this.state = {
      lastUpdatedOnPlacement: "bottom-start",
      showSpinner: false,
      loading: false,
      showAlertModal: false,
      alertModalType: "",
      alertModalTitle: "",
      alertModalMessage: "",
      input: {},
      errors: {},
      viewMode: 0,
      confirm_back: 0,
      slug: "",
      title: "",
      last_updated_on: "",
      checkbox_text: "",
      body_html: "",
      status_id: true,
    };

    this.editorConfig = {
      extraButtons: ["uploadImage", "uploadImageVideo"],
      toolbarSticky: true,
      toolbarStickyOffset: 98,
    };

    this.changeHandler = this.changeHandler.bind(this);
    this.changeView = this.changeView.bind(this);
    this.setBodyHtml = this.setBodyHtml.bind(this);
  }

  async componentDidMount() {
    this.uploadImageButton();
    this.uploadVideoButton();
    this.getData();
  }

  async getData() {
    this.setState({ showSpinner: true, loading: true });
    let res = await MasterAccommodationAgreementService.getMasterAgreement();
    if (global.successStatus.includes(res.status)) {
      const d = res.data || {};
      this.setState({
        slug: d.slug || "",
        title: d.title || "",
        last_updated_on: d.last_updated_on ? Moment(d.last_updated_on, "YYYY-MM-DD").toDate() : "",
        checkbox_text: d.checkbox_text || "",
        body_html: d.body_html || "",
        status_id: d.status_id === 2 ? false : true,
      });
    }
    this.setState({ showSpinner: false, loading: false });
  }

  uploadImageButton = () => {
    Jodit.defaultOptions.controls.uploadImage = {
      name: "Upload image",
      exec: async (editor) => {
        await this.imageUpload(editor);
      },
    };
  };

  imageUpload = (editor) => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async function () {
      const imageFile = input.files[0];
      if (!imageFile) return;
      if (!imageFile.name.match(/\.(jpg|jpeg|png)$/)) return;
      let fileData = new FormData();
      fileData.append("file", imageFile, imageFile.name);
      fileData.append("fileName", imageFile.name);
      fileData.append("folderName", "jodit");
      const response = await FileUploadService.uploadFromEditor(fileData);
      if (global.successStatus.includes(response.status)) {
        this.insertImage(editor, response.data.filePath);
      }
    }.bind(this);
  };

  insertImage = (editor, url) => {
    const image = editor.selection.j.createInside.element("img");
    image.setAttribute("src", url);
    editor.selection.insertNode(image);
  };

  uploadVideoButton = () => {
    Jodit.defaultOptions.controls.uploadImageVideo = {
      name: "Upload video",
      exec: async (editor) => {
        await this.videoUpload(editor);
      },
    };
  };

  videoUpload = (editor) => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "video/*");
    input.click();
    input.onchange = async function () {
      const videoFile = input.files[0];
      if (!videoFile) return;
      if (!videoFile.name.match(/\.(mp3|mp4|mov)$/)) return;
      let fileData = new FormData();
      fileData.append("file", videoFile, videoFile.name);
      fileData.append("fileName", videoFile.name);
      fileData.append("folderName", "jodit");
      const response = await FileUploadService.uploadFromEditor(fileData);
      if (global.successStatus.includes(response.status)) {
        this.insertVideo(editor, response.data.filePath);
      }
    }.bind(this);
  };

  insertVideo = (editor, url) => {
    const video = editor.selection.j.createInside.element("video");
    video.setAttribute("src", url);
    editor.selection.insertNode(video);
  };

  getResponsivePlacement = (ref) => {
    if (!ref.current) return "bottom-start";

    const rect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const CALENDAR_HEIGHT_ESTIMATE = 330;
    const CALENDAR_WIDTH_ESTIMATE = 300;

    if (spaceBelow >= CALENDAR_HEIGHT_ESTIMATE || window.innerWidth <= 767) {
      return "bottom-start";
    }

    const spaceRight = window.innerWidth - rect.right;
    const spaceLeft = rect.left;

    if (spaceRight >= CALENDAR_WIDTH_ESTIMATE) return "right-start";
    if (spaceLeft >= CALENDAR_WIDTH_ESTIMATE) return "left-start";
    return spaceRight >= spaceLeft ? "right-start" : "left-start";
  };

  checkLastUpdatedOnPlacement = () => {
    this.setState({
      lastUpdatedOnPlacement: this.getResponsivePlacement(this.lastUpdatedOnInputGroupRef),
    });
  };

  changeHandler = (event) => {
    let input = this.state.input;
    input[event.target.name] = event.target.value;
    this.setState({ [event.target.name]: event.target.value, input: input });
    this.validator.showMessageFor(event.target.name);
    this.setState({ confirm_back: 1 });
  };

  setBodyHtml(value) {
    this.setState({ body_html: value, confirm_back: 1 });
  }

  changeView() {
    if (this.state.viewMode === 1) {
      this.setState({ viewMode: 0, confirm_back: 0 });
      this.getData();
    } else {
      this.setState({ viewMode: 1, confirm_back: 0 });
    }
  }

  saveAgreement = async () => {
    if (this.validator.allValid()) {
      this.setState({ showSpinner: true, isSubmit: true });

      let inputData = {
        title: this.state.title,
        last_updated_on: this.state.last_updated_on
          ? Moment(this.state.last_updated_on).format("YYYY-MM-DD")
          : "",
        checkbox_text: this.state.checkbox_text,
        body_html: this.state.body_html,
        status_id: this.state.status_id ? 1 : 2,
      };

      let res = await MasterAccommodationAgreementService.updateMasterAgreement(
        this.state.slug,
        inputData
      );

      if (global.successStatus.includes(res.status)) {
        this.setState({
          showAlertModal: true,
          alertModalType: "success",
          alertModalTitle: "Success",
          alertModalMessage: res.message ? res.message : "Updated successfully",
        });
        this.getData();
        this.setState({ viewMode: 0 });
      } else {
        if (
          res.data &&
          res.data.result &&
          res.data.result.errorDetail !== null
        ) {
          let errors = {};
          res.data.result.errorDetail.forEach((item) => {
            errors[item.errorField] = item.errorMessage[0];
          });
          this.setState({ errors: errors });
        }
      }

      this.setState({ showSpinner: false, isSubmit: false });
      setTimeout(
        () => this.setState({ showAlertModal: false }),
        global.alert_close_time
      );
    } else {
      this.validator.showMessages();
    }
  };

  getTitle() {
    return "Check-in Accommodation Agreement";
  }

  render() {
    return (
      <>
        <main>
          <Container className="innter-container">
            <div className="d-flex flex-column">
              <div className="row row-xs mg-t-0 pd-t-0 pd-lg-l-15 pd-xs-l-0">
                <div className="col-md-2 left">
                  <div className="child-menu-section">
                    <aside>
                      <MasterSidebar />
                    </aside>
                  </div>
                </div>

                <div className="col-md-10 right settingsidebarNav">
                  <div className="col-md-12 mg-t-20 mg-b-10"></div>
                  <div className="col-md-12 mg-t-0 mg-b-10 pd-t-0 pd-b-10 pd-l-15 pd-r-0">
                    <div className="dashbaord-pageHeadertitle d-flex justify-content-between align-items-center flex-wrap mg-b-0">
                      <h3>{this.getTitle()}</h3>
                      <div className="d-flex justify-content-end pd-l-0 pd-r-0 mg-b-0">
                        {this.state.viewMode === 0 ? (
                          global.userPermissions.checkPermission(
                            "master-data-update"
                          ) && (
                            <button
                              className="btn-success-outline-small"
                              onClick={this.changeView}
                            >
                              <img src={editIcon} alt="" />
                              Edit
                            </button>
                          )
                        ) : (
                          <div className="d-flex">
                            <button
                              type="button"
                              className="btn-success-outline-small"
                              onClick={() => {this.changeView()}}
                            >
                              Cancel
                            </button>
                            {global.userPermissions.checkPermission(
                              "master-data-update"
                            ) && (
                              <button
                                className="btn-success ml-3"
                                onClick={this.saveAgreement}
                              >
                                Save
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="scrolling-carousel pd-xs-l-0 pd-xs-r-0 pd-lg-l-30 pd-lg-r-30">
                    <div className="dashboardRightcard dashbaord-pageHeadertitle- background-none pt-0 pd-l-30 pd-r-30 swipersectionBtns">
                      <div className="col-md-12 pd-l-15 pd-r-0 mg-t-0">
                        <Alerts
                          show={this.state.showAlertModal}
                          type={this.state.alertModalType}
                          title={this.state.alertModalTitle}
                          message={this.state.alertModalMessage}
                        />
                      </div>

                      {this.state.viewMode === 0 && (
                        <div className="m-0">
                          <div className="row align-items-start pd-t-10">
                            <div className="col-md-4 mg-b-15">
                              <p className="az-profile-name-text pl-0">Title</p>
                              <p>{this.state.title || "-"}</p>
                            </div>
                            <div className="col-md-4 mg-b-15">
                              <p className="az-profile-name-text pl-0">Last Updated On</p>
                              <p>{this.state.last_updated_on ? Moment(this.state.last_updated_on).format("MM/DD/YY") : "-"}</p>
                            </div>
                            <div className="col-md-4 mg-b-15">
                              <p className="az-profile-name-text pl-0">Checkbox Label</p>
                              <p>{this.state.checkbox_text || "-"}</p>
                            </div>
                          </div>

                          <div className="row pd-t-15">
                            <div className="col-md-12">
                              <p className="az-profile-name-text pl-0">Agreement Body</p>
                              <div className="para">
                                {this.state.body_html
                                  ? renderHTML("" + this.state.body_html)
                                  : "-"}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {this.state.viewMode === 1 && (
                        <div className="m-0">
                          <div className="row align-items-center pd-t-10">
                            <div className="col-sm-12 col-md-3 mg-b-15 pd-xs-l-0 pd-xs-r-0 pd-lg-l-15 pd-lg-r-15">
                              <label className="form-label text-left pd-b-5">Title</label>
                              <input
                                type="text"
                                className="form-control"
                                name="title"
                                value={this.state.title}
                                onChange={this.changeHandler}
                              />
                              {this.state.errors.title ? (
                                <div className="text-danger">{this.state.errors.title}</div>
                              ) : null}
                            </div>
                            <div
                              className="col-sm-12 col-md-3 mg-b-15 pd-xs-l-0 pd-xs-r-0 pd-lg-l-15 pd-lg-r-15"
                              ref={this.lastUpdatedOnInputGroupRef}
                            >
                              <label className="form-label text-left pd-b-5">Last Updated On</label>
                              <DatePicker
                                className="form-control max_width_100"
                                disabledKeyboardNavigation
                                strictParsing
                                scrollableYearDropdown
                                autoComplete="off"
                                showMonthDropdown
                                showYearDropdown
                                yearDropdownItemNumber={global.yearDropdownItemNumber}
                                name="last_updated_on"
                                dateFormat="MM/dd/yy"
                                selected={this.state.last_updated_on}
                                onChange={(data) =>
                                  this.changeHandler({
                                    target: {
                                      type: "date",
                                      name: "last_updated_on",
                                      value: data,
                                    },
                                  })
                                }
                                popperPlacement={this.state.lastUpdatedOnPlacement}
                                onCalendarOpen={this.checkLastUpdatedOnPlacement}
                                popperModifiers={{
                                  preventOverflow: {
                                    enabled: true,
                                    escapeWithReference: false,
                                  },
                                }}
                              />
                            </div>
                            <div className="col-sm-12 col-md-3 mg-b-15 pd-xs-l-0 pd-xs-r-0 pd-lg-l-15 pd-lg-r-15">
                              <label className="form-label text-left pd-b-5">Checkbox Label</label>
                              <input
                                type="text"
                                className="form-control"
                                name="checkbox_text"
                                value={this.state.checkbox_text}
                                onChange={this.changeHandler}
                              />
                            </div>
                            <div className="col-sm-12 col-md-3 mg-b-15 pd-xs-l-0 pd-xs-r-0 pd-lg-l-15 pd-lg-r-15">
                              <label className="form-label text-left pd-b-5">Status</label>
                              <div
                                className={`${this.state.status_id ? "az-toggle on" : "az-toggle"}`}
                                onClick={() =>
                                  this.setState({
                                    status_id: !this.state.status_id,
                                    confirm_back: 1,
                                  })
                                }
                              >
                                <span></span>
                              </div>
                            </div>
                          </div>

                          <div className="row align-items-center pd-t-15">
                            <div className="col-sm-12 col-md-12 mg-b-15 pd-xs-l-0 pd-xs-r-0 pd-lg-l-15 pd-lg-r-15">
                              <label className="form-label text-left pd-b-10">Agreement Body</label>
                              <JoditEditor
                                config={this.editorConfig}
                                ref={null}
                                name="body_html"
                                id="body_html"
                                value={this.state.body_html}
                                onBlur={(newContent) => this.setBodyHtml(newContent)}
                              />
                            </div>
                          </div>

                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </main>
      </>
    );
  }
}

export default MasterAccommodationAgreement;
